import React, { useState, useEffect } from 'react'
import { ACTIVITY_TYPES, formatTimeSpan } from '../helpers'
import { X } from 'lucide-react'
import { Output, Variable } from '../types'
import { useSelector } from 'react-redux'

interface StepSettingsModalProps {
  selectedStep: any
  showSettings: boolean
  workflowVariables: Variable[]
  workflowOutputs?: Output[]
  onSave: (parameters: any) => void
  onCancel: () => void
}

// Helper functions to extract duration and unit from TimeSpan format
const extractDurationFromTimeSpan = (timeSpan: string): string => {
  if (!timeSpan) return '10'

  // TimeSpan format: HH:MM:SS
  const parts = timeSpan.split(':')
  if (parts.length !== 3) return '10'

  const hours = parseInt(parts[0])
  const minutes = parseInt(parts[1])
  const seconds = parseInt(parts[2])

  if (hours > 0) return hours.toString()
  if (minutes > 0) return minutes.toString()
  return seconds.toString()
}

const extractUnitFromTimeSpan = (timeSpan: string): string => {
  if (!timeSpan) return 'seconds'

  // TimeSpan format: HH:MM:SS
  const parts = timeSpan.split(':')
  if (parts.length !== 3) return 'seconds'

  const hours = parseInt(parts[0])
  const minutes = parseInt(parts[1])
  const seconds = parseInt(parts[2])

  if (hours > 0) {
    return hours >= 24 ? 'days' : 'hours'
  }
  if (minutes > 0) return 'minutes'
  return 'seconds'
}

const eventNames = [
  { id: 1, label: 'User Signed Up', value: 'UserSignedUp' },
  { id: 2, label: 'Contact Created', value: 'ContactCreated' },
  { id: 3, label: 'Contact Responded', value: 'ContactResponded' },
  { id: 4, label: 'Appointment Created', value: 'AppointmentCreated' },
  { id: 5, label: 'Appointment Deleted', value: 'AppointmentDeleted' },
  { id: 6, label: 'Summary Conversation', value: 'SummaryConversation' },
]

const StepSettingsModal: React.FC<StepSettingsModalProps> = ({
  selectedStep,
  showSettings,
  workflowVariables = [],
  workflowOutputs = [],
  onSave,
  onCancel
}) => {
  const [formValues, setFormValues] = useState<any>({})
  const [workflowList, setWorkflowList] = useState<any[]>([])

  const workflows = useSelector((state: any) => state.Workflow.workflows);

  // Initialize form values when selectedStep changes
  useEffect(() => {
    if (selectedStep?.settings) {
      setFormValues({ ...selectedStep.settings })
    } else {
      setFormValues({})
    }
  }, [selectedStep])

  useEffect(() => {
    if (workflows?.length > 0) {
      const workflowOptions = workflows.map((workflow: any) => ({
        id: workflow?.definitionId,
        name: workflow?.name
      }));

      setWorkflowList(workflowOptions);
    }
  }, [workflows])

  if (!showSettings || !selectedStep) return null

  // Helper function to update form values
  const updateFormValue = (key: string, value: any) => {
    setFormValues((prev: any) => ({ ...prev, [key]: value }))
  }

  const handleSave = () => {
    const parameters: any = {}

    if (selectedStep.type === ACTIVITY_TYPES.DELAY || selectedStep.type === ACTIVITY_TYPES.TIMER) {
      const duration = parseInt((document.getElementById('duration') as HTMLInputElement)?.value || '0')
      const unit = (document.getElementById('unit') as HTMLSelectElement)?.value || 'seconds'
      if (duration > 0) {
        parameters.duration = formatTimeSpan(duration, unit)
      }
    }
    else if (selectedStep.type === ACTIVITY_TYPES.SEND_EMAIL_MARKETING) {
      const recipientEmail = (document.getElementById('recipientEmail') as HTMLInputElement)?.value
      const recipientName = (document.getElementById('recipientName') as HTMLInputElement)?.value
      if (recipientEmail) parameters.recipientEmail = recipientEmail
      if (recipientName) parameters.recipientName = recipientName
    }
    else if (selectedStep.type === ACTIVITY_TYPES.SEND_MESSAGE_REMINDER) {
      const message = (document.getElementById('message') as HTMLTextAreaElement)?.value
      if (message) parameters.message = message
    }
    else if (selectedStep.type === ACTIVITY_TYPES.WRITE_LINE) {
      if (formValues.text) parameters.text = formValues.text
      const message = (document.getElementById('message') as HTMLTextAreaElement)?.value
      if (message) parameters.message = message
    }
    else if (selectedStep.type === ACTIVITY_TYPES.READ_LINE) {
      const prompt = (document.getElementById('prompt') as HTMLInputElement)?.value
      if (prompt) parameters.prompt = prompt
    }
    else if (selectedStep.type === ACTIVITY_TYPES.IF) {
      const condition = (document.getElementById('condition') as HTMLInputElement)?.value
      const conditionType = (document.getElementById('conditionType') as HTMLSelectElement)?.value
      const thenMessage = (document.getElementById('thenMessage') as HTMLTextAreaElement)?.value
      const elseMessage = (document.getElementById('elseMessage') as HTMLTextAreaElement)?.value

      if (condition) parameters.condition = condition
      if (conditionType) parameters.conditionType = conditionType
      if (thenMessage) parameters.thenMessage = thenMessage
      if (elseMessage) parameters.elseMessage = elseMessage
    }
    else if (selectedStep.type === ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST) {
      const title = (document.getElementById('title') as HTMLInputElement)?.value
      if (title) parameters.title = title
      const url = (document.getElementById('url') as HTMLInputElement)?.value
      if (url) parameters.url = url
      const method = (document.getElementById('method') as HTMLSelectElement)?.value
      if (method) parameters.method = method
      // const content = (document.getElementById('content') as HTMLTextAreaElement)?.value
      // if (content) parameters.content = content
      const contentType = (document.getElementById('contentType') as HTMLSelectElement)?.value
      if (contentType) parameters.contentType = contentType
      if (localStorage.getItem('authUser')) {
        parameters.authorization = `Bearer ${localStorage.getItem('authUser')}`.trim()
      }
      if (formValues.parsedContent) parameters.parsedContent = formValues.parsedContent
      if (formValues.content) parameters.content = formValues.content
    }
    else if (selectedStep.type === ACTIVITY_TYPES.WRITE_HTTP_RESPONSE) {
      if (formValues.contentValue) parameters.contentValue = formValues.contentValue
      const statusCode = (document.getElementById('statusCode') as HTMLSelectElement)?.value
      if (statusCode) parameters.statusCode = statusCode
    }
    else if (selectedStep.type === ACTIVITY_TYPES.CRON) {
      const cronExpression = (document.getElementById('cronExpression') as HTMLInputElement)?.value
      if (cronExpression) parameters.cronExpression = cronExpression
    }
    else if (selectedStep.type === ACTIVITY_TYPES.DISPATCH_WORKFLOW) {
      if (formValues.workflowDefinitionId) parameters.workflowDefinitionId = formValues.workflowDefinitionId
      const input = (document.getElementById('input') as HTMLInputElement)?.value
      if (input) parameters.input = input
    }
    else if (selectedStep.type === ACTIVITY_TYPES.SET_OUTPUT) {
      if (formValues.output) parameters.output = formValues.output
      const outputValue = (document.getElementById('outputValue') as HTMLInputElement)?.value
      if (outputValue) parameters.outputValue = outputValue
    }
    else if (selectedStep.type === ACTIVITY_TYPES.HTTP_ENDPOINT) {
      if (formValues.output) parameters.output = formValues.output
      const supportedMethods = (document.getElementById('supportedMethods') as HTMLInputElement)?.value
      if (supportedMethods) parameters.supportedMethods = supportedMethods
      const path = (document.getElementById('path') as HTMLInputElement)?.value
      if (path) parameters.path = path
      if (formValues.routeData) parameters.routeData = formValues.routeData
    }
    else if (selectedStep.type === ACTIVITY_TYPES.SET_VARIABLE) {
      if (formValues.variable) parameters.variable = formValues.variable
      const value = (document.getElementById('value') as HTMLInputElement)?.value
      if (value) parameters.value = value
    }
    else if (selectedStep.type === ACTIVITY_TYPES.START_AT) {
      const dateTime = (document.getElementById('dateTime') as HTMLInputElement)?.value
      if (dateTime) parameters.dateTime = dateTime
    }
    else if (selectedStep.type === ACTIVITY_TYPES.EVENT) {
      if (formValues.eventName) parameters.eventName = formValues.eventName
      if (formValues.result) parameters.result = formValues.result
    }

    onSave(parameters)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="relative bg-white rounded-lg w-full max-w-3xl h-[70vh] dark:bg-zink-600 flex flex-col">
        <div className="p-6 border-b border-slate-200 dark:border-zink-500">
          <h3 className="text-lg font-medium text-slate-800 dark:text-zink-50 cursor-pointer">
            Configure {selectedStep.title}
          </h3>
          <span onClick={onCancel} className='absolute top-4 right-4 hover:text-slate-600'><X /></span>
        </div>

        <div className="p-6 overflow-y-auto flex-grow">

          {/* Delay Settings */}
          {selectedStep.type === ACTIVITY_TYPES.DELAY && (
            <div className="flex flex-col gap-2">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Duration
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter duration"
                  defaultValue={selectedStep.settings?.duration ?
                    extractDurationFromTimeSpan(selectedStep.settings.duration) : '10'}
                  id="duration"
                />
              </div>
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Unit
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  defaultValue={selectedStep.settings?.duration ?
                    extractUnitFromTimeSpan(selectedStep.settings.duration) : 'seconds'}
                  id="unit"
                >
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          )}

          {/* Write Line Settings */}
          {selectedStep.type === ACTIVITY_TYPES.WRITE_LINE && (
            <div className="flex flex-col gap-2">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Message
                </label>
                <textarea
                  className="!mt-0 w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  rows={4}
                  placeholder="Enter message to display"
                  defaultValue={selectedStep.settings?.message || ''}
                  id="message"
                />
              </div>

              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Text
                </label>
                <select
                  id="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.text || ''}
                  onChange={(e) => updateFormValue('text', e.target.value)}
                >
                  <option value="">Select a variable</option>
                  {workflowVariables.map((variable) => (
                    <option key={variable.id} value={variable.name}>
                      {variable.name} ({variable.typeName})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Read Line Settings */}
          {selectedStep.type === ACTIVITY_TYPES.READ_LINE && (
            <div className="flex flex-col gap-2">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Prompt (Optional)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter prompt text"
                  defaultValue={selectedStep.settings?.prompt || ''}
                  id="prompt"
                />
              </div>
            </div>
          )}

          {/* Send Email Marketing Settings */}
          {(selectedStep.type === ACTIVITY_TYPES.SEND_EMAIL_MARKETING) && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Recipient Email
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter recipient email"
                  defaultValue={selectedStep.settings?.recipientEmail || ''}
                  id="recipientEmail"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Recipient Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter recipient name"
                  defaultValue={selectedStep.settings?.recipientName || ''}
                  id="recipientName"
                />
              </div>
            </div>
          )}


          {/* Flow Send HTTP Request Settings */}
          {(selectedStep.type === ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST) && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter title"
                  defaultValue={selectedStep.settings?.title || ''}
                  id="title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  URL
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter URL"
                  defaultValue={selectedStep.settings?.url || ''}
                  id="url"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Method
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  defaultValue={selectedStep.settings?.method || 'GET'}
                  id="method"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Parsed Content
                </label>
                <select
                  id="parsedContent"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.parsedContent || ''}
                  onChange={(e) => updateFormValue('parsedContent', e.target.value)}
                >
                  <option value="">Select a variable</option>
                  {workflowVariables.map((variable) => (
                    <option key={variable.id} value={variable.name}>
                      {variable.name} ({variable.typeName})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Content
                </label>
                <select
                  id="content"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.content || ''}
                  onChange={(e) => updateFormValue('content', e.target.value)}
                >
                  <option value="">Select a variable</option>
                  {workflowVariables.map((variable) => (
                    <option key={variable.id} value={variable.name}>
                      {variable.name} ({variable.typeName})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Content Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  defaultValue={selectedStep.settings?.contentType || 'application/json'}
                  id="contentType"
                >
                  <option value="application/json">JSON</option>
                  <option value="application/xml">XML</option>
                  <option value="text/plain">Plain Text</option>
                </select>
              </div>
            </div>
          )}

          {/* Write HTTP Response Settings */}
          {(selectedStep.type === ACTIVITY_TYPES.WRITE_HTTP_RESPONSE) && (
            <div className="flex flex-col gap-2">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Status Code
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  defaultValue={selectedStep.settings?.method || 'GET'}
                  id="statusCode"
                >
                  <option value="OK">OK</option>
                  <option value="NotFound">Not Found</option>
                </select>
              </div>

              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Content Value
                </label>
                <select
                  id="contentValue"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.contentValue || ''}
                  onChange={(e) => updateFormValue('contentValue', e.target.value)}
                >
                  <option value="">Select a variable</option>
                  {workflowVariables.map((variable) => (
                    <option key={variable.id} value={variable.name}>
                      {variable.name} ({variable.typeName})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Cron Expression */}
          {(selectedStep.type === ACTIVITY_TYPES.CRON) && (
            <div className="flex flex-col gap-2">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Cron Expression
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter cron expression"
                  defaultValue={selectedStep.settings?.cronExpression || ''}
                  id="cronExpression"
                />
              </div>
            </div>
          )}

          {/* Dispatch Workflow Settings */}
          {selectedStep.type === ACTIVITY_TYPES.DISPATCH_WORKFLOW && (
            <div className="flex flex-col gap-2">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Workflow Definition
                </label>
                <select
                  id="workflowDefinitionId"
                  className="form-input overflow-auto border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  value={formValues.workflowDefinitionId || ''}
                  onChange={(e) => updateFormValue('workflowDefinitionId', e.target.value)}
                >
                  <option value="">Select Workflow</option>
                  {workflowList.map((workflow) => (
                    <option key={workflow.id} value={workflow.id}>
                      {workflow.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Input
                </label>
                <textarea
                  className="!mt-0 w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  rows={4}
                  placeholder="Enter input data"
                  defaultValue={selectedStep.settings?.input || ''}
                  id="input"
                />
              </div>
            </div>
          )}

          {/* Set Output */}
          {(selectedStep.type === ACTIVITY_TYPES.SET_OUTPUT) && (
            <div className="flex flex-col gap-2">
              <>
                <div className='flex-1'>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Output
                  </label>
                  <select
                    id="output"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={formValues.output || ''}
                    onChange={(e) => updateFormValue('output', e.target.value)}
                  >
                    <option value="">Select a output</option>
                    {workflowOutputs.map((output) => (
                      <option key={output.id} value={output.name}>
                        {output.name} ({output.type})
                      </option>
                    ))}
                  </select>
                </div>
                <div className='flex-1'>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Output Value
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    placeholder="Enter output value"
                    defaultValue={selectedStep.settings?.outputValue || ''}
                    id="outputValue"
                  />
                </div>
              </>
            </div>
          )}

          {/* HTTP Endpoint Settings */}
          {(selectedStep.type === ACTIVITY_TYPES.HTTP_ENDPOINT) && (
            <div className="  flex flex-col gap-2">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Path
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter endpoint path"
                  defaultValue={selectedStep.settings?.path || ''}
                  id="path"
                />
              </div>
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Method
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  defaultValue={selectedStep.settings?.supportedMethods || 'GET'}
                  id="supportedMethods"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Route Data
                </label>
                <select
                  id="routeData"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.routeData || ''}
                  onChange={(e) => updateFormValue('routeData', e.target.value)}
                >
                  <option value="">Select a variable</option>
                  {workflowVariables.map((variable) => (
                    <option key={variable.id} value={variable.name}>
                      {variable.name} ({variable.typeName})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Set Variable Settings */}
          {(selectedStep.type === ACTIVITY_TYPES.SET_VARIABLE) && (
            <div className="flex flex-1 gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Variable
                </label>
                <select
                  id="variable"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.variable || ''}
                  onChange={(e) => updateFormValue('variable', e.target.value)}
                >
                  <option value="">Select a variable</option>
                  {workflowVariables.map((variable) => (
                    <option key={variable.id} value={variable.name}>
                      {variable.name} ({variable.typeName})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Value
                </label>
                <textarea
                  className="!mt-0 w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter variable value"
                  defaultValue={selectedStep.settings?.value || ''}
                  id="value"
                />
              </div>
            </div>
          )}

          {/* Set Variable Settings */}
          {(selectedStep.type === ACTIVITY_TYPES.START_AT) && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Date time
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  placeholder="Enter date time"
                  defaultValue={selectedStep.settings?.dateTime || ''}
                  id="dateTime"
                />
              </div>
            </div>
          )}

          {/* Event Settings */}
          {selectedStep.type === ACTIVITY_TYPES.EVENT && (
            <div className="flex flex-col gap-2 flex-1">
              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Event Name
                </label>
                <select
                  id="eventName"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.eventName || ''}
                  onChange={(e) => updateFormValue('eventName', e.target.value)}
                >
                  <option value="">Select an event</option>
                  {eventNames.map((event) => (
                    <option key={event.id} value={event.value}>
                      {event.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className='flex-1'>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Text
                </label>
                <select
                  id="result"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={formValues.result || ''}
                  onChange={(e) => updateFormValue('result', e.target.value)}
                >
                  <option value="">Select a variable</option>
                  {workflowVariables.map((variable) => (
                    <option key={variable.id} value={variable.name}>
                      {variable.name} ({variable.typeName})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

        </div>
        <div className="p-6 border-t border-slate-200 dark:border-zink-500">
          <div className="flex float-end gap-3">
            <button
              onClick={onCancel}
              className=" px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className=" px-4 py-2 bg-custom-500 text-white rounded-lg hover:bg-custom-600 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StepSettingsModal
